/**
 * `useViewerAccess` — which pages the backend allows this employee to open.
 *
 * The React half of `src/lib/viewer-access.ts`. The rail reads it to leave out
 * a page the viewer cannot open. The backend also protects each page's data.
 *
 * The caller blocks the rail and route while loading or after a failure.
 *
 * The key names the viewer. The query client outlives a sign-out, and a second
 * employee signing in on the same tab must not inherit the first one's answer.
 */

import { useQuery } from '@tanstack/react-query'

import { fetchViewerAccess } from '../lib/viewer-access.ts'

/**
 * How long a fetched answer is served without asking again.
 *
 * The backend caches group membership for ten minutes. Refresh the rail before
 * a second ten-minute window elapses after a group change.
 */
export const VIEWER_ACCESS_STALE_TIME_MS = 5 * 60 * 1000

/**
 * Read the signed-in employee's allowed pages.
 *
 * @param viewer - Who is signed in. Part of the cache key.
 * @returns The access query, including its loading and error states.
 */
export function useViewerAccess(viewer: string) {
  return useQuery({
    queryKey: ['viewer-access', viewer],
    queryFn: fetchViewerAccess,
    staleTime: VIEWER_ACCESS_STALE_TIME_MS,
    refetchInterval: VIEWER_ACCESS_STALE_TIME_MS,
    retry: 2,
    retryDelay: attempt => 250 * (attempt + 1)
  })
}
